import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Power } from 'lucide-react';
import { toast } from 'react-toastify';
import { stockSyncAPI } from '../../services/api';

const STORE_NAMES = {
  amazon: { 1: 'GRS Imports', 3: 'Ibexworkwear' },
  ebay: { 1: 'Maq Trading', 2: 'Hawk Clothing', 3: 'Prospect Catering', 4: 'Lovely Homes', 5: 'Ibex Work Wear' },
};
const storeLabel = (a) => a.platform === 'shopify' ? 'Shopify' : `${a.platform === 'amazon' ? 'Amazon' : 'eBay'} · ${STORE_NAMES[a.platform]?.[a.store_id] || 'store ' + a.store_id}`;
const when = (ms) => ms ? new Date(Number(ms)).toLocaleString() : 'never';

export default function StockSync() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [skuText, setSkuText] = useState('');
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([stockSyncAPI.getSettings(), stockSyncAPI.getActivity(100)]);
      setData(s.data);
      setForm(f => f || {
        buffer: s.data.settings.buffer, mode: s.data.settings.mode,
        restore_on_cancel: !!s.data.settings.restore_on_cancel, require_fresh_baseline: !!s.data.settings.require_fresh_baseline,
      });
      setSkuText(t => t || s.data.selected_skus.join('\n'));
      setActivity(a.data.activity || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not load stock sync');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data || !form) return <div className="text-gray-400">Loading…</div>;
  const { settings, counts, amazon_listener: listener, shopify_webhook: shopify = {}, channels = {} } = data;
  const ch = (name) => channels[name] || {};
  const received = (name) => `${Number(ch(name).received) || 0} received${ch(name).last_received_at ? `, last ${when(ch(name).last_received_at)}` : ''}`;
  const enabled = !!settings.enabled;

  const act = async (fn, okMessage) => {
    setBusy(true);
    try { const r = await fn(); if (okMessage) toast.success(typeof okMessage === 'function' ? okMessage(r) : okMessage); await load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Something went wrong'); }
    finally { setBusy(false); }
  };

  const toggle = () => {
    if (!enabled) {
      const scope = form.mode === 'all' ? 'ALL products' : `the ${counts.selected} selected product(s)`;
      if (!window.confirm(`Turn stock sync ON for ${scope}?\n\nFrom now on, every new order reduces the product's master stock and that number is pushed to its other stores. Orders already in the system are not counted.`)) return;
    }
    act(() => stockSyncAPI.saveSettings({ ...form, enabled: !enabled }), enabled ? 'Stock sync turned off' : 'Stock sync turned on');
  };

  const saveSkus = () => act(
    () => stockSyncAPI.saveSelected(skuText.split(/[\n,]+/).map(x => x.trim()).filter(Boolean)),
    (r) => `${r.data.selected} product(s) selected` + (r.data.not_found.length ? ` — not found: ${r.data.not_found.join(', ')}` : '')
  );

  const box = 'bg-white/5 border border-white/10 rounded-2xl p-6 mb-6';
  const input = 'px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/50';

  return (
    <div className="max-w-6xl mx-auto w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Stock Sync</h1>
          <p className="text-gray-400">When a product sells on one store, reduce its stock on every other store it is listed on</p>
        </div>
        <button onClick={toggle} disabled={busy}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50 ${enabled ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-white text-gray-900 hover:bg-gray-200'}`}>
          <Power size={18} /> {enabled ? 'ON — click to turn off' : 'OFF — click to turn on'}
        </button>
      </div>

      <div className={box}>
        <h2 className="text-white font-semibold mb-4">Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-gray-400">Sync</p><p className={enabled ? 'text-green-400 font-medium' : 'text-gray-300 font-medium'}>{enabled ? `On since ${when(settings.enabled_at)}` : 'Off'}</p></div>
          <div><p className="text-gray-400">Last check</p><p className="text-white">{when(settings.last_run_at)}</p></div>
          <div><p className="text-gray-400">Products with stock set since turning on</p><p className="text-white">{enabled ? counts.fresh_baseline : '—'}</p></div>
          <div><p className="text-gray-400">Waiting to be re-pushed</p><p className="text-white">{counts.pending}</p></div>
        </div>
        {settings.last_run_note && <p className="text-gray-400 text-sm mt-3">Last check: {settings.last_run_note}</p>}
        <p className="text-sm mt-3 text-gray-400">
          Amazon instant notifications:{' '}
          {!listener.configured ? <span className="text-yellow-300">not configured on the server (orders are still picked up by the regular order sync)</span>
            : listener.lastError ? <span className="text-red-300">error — {listener.lastError}</span>
            : <span className="text-green-400">listening (last checked {when(ch('amazon').last_check_at || listener.lastPollAt)}, {received('amazon')})</span>}
        </p>
        <p className="text-sm mt-1 text-gray-400">
          Shopify instant notifications:{' '}
          {shopify.registered
            ? <span className="text-green-400">connected ({received('shopify')})</span>
            : <span className="text-yellow-300">not connected{shopify.error ? ` — ${shopify.error}` : ''} (orders are still picked up every 10 minutes)</span>}
          {!shopify.registered && (
            <button onClick={() => act(() => stockSyncAPI.registerShopifyWebhook(), 'Tried to connect Shopify notifications')} disabled={busy} className="ml-2 underline text-white">Try again</button>
          )}
        </p>
        <p className="text-sm mt-1 text-gray-400">
          Amazon orders (checked every 2 minutes):{' '}
          {ch('amazon_check').last_error
            ? <span className="text-red-300">error — {ch('amazon_check').last_error}</span>
            : ch('amazon_check').last_check_at
              ? <span className="text-green-400">checking (last checked {when(ch('amazon_check').last_check_at)}, {received('amazon_check')})</span>
              : <span className="text-gray-400">waiting for the first check</span>}
        </p>
        <p className="text-sm mt-1 text-gray-400">
          eBay orders (checked every 2 minutes):{' '}
          {ch('ebay').last_error
            ? <span className="text-red-300">error — {ch('ebay').last_error}</span>
            : ch('ebay').last_check_at
              ? <span className="text-green-400">checking (last checked {when(ch('ebay').last_check_at)}, {received('ebay')})</span>
              : <span className="text-gray-400">waiting for the first check</span>}
        </p>
      </div>

      <div className={box}>
        <h2 className="text-white font-semibold mb-4">Settings</h2>
        <div className="space-y-4 text-sm text-gray-200">
          <div className="flex flex-wrap items-center gap-3">
            <span className="w-56 text-gray-300">Apply to</span>
            <select className={input + ' [color-scheme:dark]'} value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
              <option value="selected">Only the products listed below</option>
              <option value="all">All mapped products</option>
            </select>
          </div>
          {settings.adjust_mode === 'absolute' ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="w-56 text-gray-300">Safety buffer (units held back)</span>
              <input type="number" min="0" className={input + ' w-24'} value={form.buffer} onChange={e => setForm({ ...form, buffer: e.target.value })} />
              <span className="text-gray-400">Stores show master stock minus this number</span>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Each store keeps its own stock: a sale takes the quantity sold off every other store, and a cancellation puts it back.</p>
          )}
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={form.require_fresh_baseline} onChange={e => setForm({ ...form, require_fresh_baseline: e.target.checked })} />
            <span>Only sync a product after its stock has been set (import or manual) since sync was turned on <span className="text-gray-400">— recommended; stops an old or zero master quantity being pushed to the stores</span></span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={form.restore_on_cancel} onChange={e => setForm({ ...form, restore_on_cancel: e.target.checked })} />
            <span>Put stock back automatically when an order is cancelled <span className="text-gray-400">— Amazon cancellations and voided Shopify orders</span></span>
          </label>
          <button onClick={() => act(() => stockSyncAPI.saveSettings(form), 'Settings saved')} disabled={busy} className="px-5 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-200 disabled:opacity-50">Save settings</button>
        </div>
      </div>

      {form.mode === 'selected' && (
        <div className={box}>
          <h2 className="text-white font-semibold mb-1">Products to sync</h2>
          <p className="text-gray-400 text-sm mb-3">One Master SKU per line. Currently selected: {counts.selected}</p>
          <textarea className={input + ' w-full h-40 font-mono text-sm'} value={skuText} onChange={e => setSkuText(e.target.value)} placeholder={'C2NL\nC2NM\nTS2B38R'} />
          <button onClick={saveSkus} disabled={busy} className="mt-3 px-5 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-200 disabled:opacity-50">Save product list</button>
        </div>
      )}

      <div className={box}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold">Recent activity</h2>
          <button onClick={() => act(() => stockSyncAPI.runNow(), 'Checked for new orders')} disabled={busy} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50">
            <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Check now
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-white/10">
                <th className="py-2 pr-4 font-medium">Time</th><th className="py-2 pr-4 font-medium">Store</th><th className="py-2 pr-4 font-medium">Order</th>
                <th className="py-2 pr-4 font-medium">Product</th><th className="py-2 pr-4 font-medium">Qty</th><th className="py-2 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {activity.length === 0 ? (
                <tr><td colSpan={6} className="py-6 text-center text-gray-500">Nothing yet</td></tr>
              ) : activity.map(a => (
                <tr key={a.id} className="border-b border-white/5 last:border-0 align-top">
                  <td className="py-2 pr-4 text-gray-300 whitespace-nowrap">{when(a.created_at)}</td>
                  <td className="py-2 pr-4 text-gray-300 whitespace-nowrap">{storeLabel(a)}</td>
                  <td className="py-2 pr-4 text-gray-400">{a.order_id}</td>
                  <td className="py-2 pr-4 text-white">{a.master_sku || a.line_key}</td>
                  <td className="py-2 pr-4 text-gray-300">{a.quantity}</td>
                  <td className="py-2">
                    {a.action === 'sale'
                      ? <span className="text-green-400">Stock reduced{a.cancel_applied ? ', then restored (cancelled)' : ''}<span className="text-gray-400">{a.last_note ? ` — ${a.last_note}` : ''}</span></span>
                      : <span className="text-gray-400">Skipped — {a.note}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
